import { supabase } from '@/integrations/supabase/client';
import { generateEmbedding, generateEmbedding1536 } from '@/lib/embeddings';
import { extractVaultLinks } from '@/lib/librarian/linkParser';
import type {
  KnowledgeBaseVault,
  KnowledgeBaseVaultLink,
  KnowledgeBaseVaultPage,
  KnowledgeBaseVaultRun,
  KnowledgeGraphEdge,
  KnowledgeGraphEdgeType,
  KnowledgeGraphNode,
  KnowledgeGraphNodeType,
  VaultPageType,
  VaultRunStatus,
  VaultRunType,
} from '@/lib/librarian/types';

const pageTypes: VaultPageType[] = ['index', 'log', 'overview', 'source', 'topic', 'entity', 'synthesis', 'lint-report'];
const runTypes: VaultRunType[] = ['ingest', 'query-save', 'lint', 'rebuild'];
const runStatuses: VaultRunStatus[] = ['running', 'completed', 'failed'];
const graphNodeTypes: KnowledgeGraphNodeType[] = ['page', 'source', 'entity', 'topic', 'run'];
const graphEdgeTypes: KnowledgeGraphEdgeType[] = [
  'wiki_link',
  'references_source',
  'mentions',
  'related_to',
  'generated_by_run',
  'processed_source',
  'derived_from_page',
  'co_mentions',
];

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function asNumberArray(value: unknown): number[] | null {
  return Array.isArray(value) ? value.filter((item): item is number => typeof item === 'number') : null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

export function slugifyVaultSegment(value: string) {
  return value
    .toLowerCase()
    .replace(/\.md$/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'untitled';
}

export function mapVault(row: any): KnowledgeBaseVault {
  return {
    id: row.id,
    knowledge_base_id: row.knowledge_base_id,
    root_path: row.root_path,
    schema_version: row.schema_version,
    librarian_enabled: row.librarian_enabled,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function mapVaultPage(row: any): KnowledgeBaseVaultPage {
  return {
    id: row.id,
    vault_id: row.vault_id,
    parent_path: row.parent_path,
    path: row.path,
    slug: row.slug,
    title: row.title,
    page_type: pageTypes.includes(row.page_type) ? row.page_type : 'overview',
    content_markdown: row.content_markdown ?? '',
    summary: row.summary,
    source_document_ids: asStringArray(row.source_document_ids),
    metadata: asRecord(row.metadata),
    embedding: asNumberArray(row.embedding),
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function mapVaultLink(row: any): KnowledgeBaseVaultLink {
  return {
    id: row.id,
    vault_id: row.vault_id,
    from_page_id: row.from_page_id,
    to_page_id: row.to_page_id,
    target_path: row.target_path,
    link_text: row.link_text,
    created_at: row.created_at,
  };
}

export function mapVaultRun(row: any): KnowledgeBaseVaultRun {
  return {
    id: row.id,
    vault_id: row.vault_id,
    run_type: runTypes.includes(row.run_type) ? row.run_type : 'ingest',
    status: runStatuses.includes(row.status) ? row.status : 'completed',
    input_document_ids: asStringArray(row.input_document_ids),
    output_page_ids: asStringArray(row.output_page_ids),
    notes: row.notes,
    created_by: row.created_by,
    created_at: row.created_at,
  };
}

export function mapKnowledgeGraphNode(row: any): KnowledgeGraphNode {
  return {
    id: row.id,
    knowledge_base_id: row.knowledge_base_id,
    vault_id: row.vault_id,
    vault_page_id: row.vault_page_id,
    source_document_id: row.source_document_id,
    vault_run_id: row.vault_run_id,
    node_key: row.node_key,
    node_type: graphNodeTypes.includes(row.node_type) ? row.node_type : 'page',
    label: row.label,
    slug: row.slug,
    summary: row.summary,
    content_excerpt: row.content_excerpt,
    metadata: asRecord(row.metadata),
    embedding: asNumberArray(row.embedding),
    created_by: row.created_by,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function mapKnowledgeGraphEdge(row: any): KnowledgeGraphEdge {
  return {
    id: row.id,
    knowledge_base_id: row.knowledge_base_id,
    from_node_id: row.from_node_id,
    to_node_id: row.to_node_id,
    edge_type: graphEdgeTypes.includes(row.edge_type) ? row.edge_type : 'related_to',
    weight: Number(row.weight ?? 1),
    evidence: row.evidence,
    metadata: asRecord(row.metadata),
    created_by: row.created_by,
    created_at: row.created_at,
  };
}

export async function fetchVault(knowledgeBaseId: string) {
  const { data, error } = await supabase
    .from('knowledge_base_vaults')
    .select('*')
    .eq('knowledge_base_id', knowledgeBaseId)
    .maybeSingle();

  if (error) throw error;
  return data ? mapVault(data) : null;
}

export async function createVault(knowledgeBaseId: string) {
  const { data, error } = await (supabase as any)
    .from('knowledge_base_vaults')
    .insert({ knowledge_base_id: knowledgeBaseId })
    .select('*')
    .single();

  if (error) throw error;
  return mapVault(data);
}

export async function fetchVaultPages(vaultId: string) {
  const { data, error } = await supabase
    .from('knowledge_base_vault_pages')
    .select('*')
    .eq('vault_id', vaultId)
    .order('path');

  if (error) throw error;
  return (data ?? []).map(mapVaultPage);
}

export async function fetchVaultExportPages(vaultId: string) {
  const { data, error } = await supabase
    .from('knowledge_base_vault_pages')
    .select('path, content_markdown')
    .eq('vault_id', vaultId)
    .order('path');

  if (error) throw error;
  return ((data ?? []) as Array<{ path?: string | null; content_markdown?: string | null }>).map((row) => ({
    path: typeof row.path === 'string' ? row.path : '',
    content_markdown: typeof row.content_markdown === 'string' ? row.content_markdown : '',
  }));
}

export async function fetchVaultPageSummaries(vaultId: string) {
  const { data, error } = await supabase
    .from('knowledge_base_vault_pages')
    .select('id, vault_id, parent_path, path, slug, title, page_type, summary, source_document_ids, metadata, created_at, updated_at')
    .eq('vault_id', vaultId)
    .order('path');

  if (error) throw error;
  return (data ?? []).map((row) => mapVaultPage({ ...(row as Record<string, unknown>), content_markdown: '' }));
}

export async function fetchVaultPageContent(pageId: string) {
  const { data, error } = await supabase
    .from('knowledge_base_vault_pages')
    .select('content_markdown')
    .eq('id', pageId)
    .single();

  if (error) throw error;
  const content = (data as { content_markdown?: string } | null)?.content_markdown;
  return typeof content === 'string' ? content : '';
}

export async function fetchVaultLinks(vaultId: string) {
  const { data, error } = await supabase
    .from('knowledge_base_vault_links')
    .select('*')
    .eq('vault_id', vaultId);

  if (error) throw error;
  return (data ?? []).map(mapVaultLink);
}

export async function fetchVaultRuns(vaultId: string) {
  const { data, error } = await supabase
    .from('knowledge_base_vault_runs')
    .select('*')
    .eq('vault_id', vaultId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []).map(mapVaultRun);
}

export async function upsertVaultPage(input: {
  vaultId: string;
  path: string;
  title: string;
  pageType: VaultPageType;
  contentMarkdown: string;
  summary?: string | null;
  sourceDocumentIds?: string[];
  metadata?: Record<string, unknown>;
}) {
  const normalizedPath = input.path.replace(/^\//, '');
  const parentPath = normalizedPath.includes('/') ? normalizedPath.split('/').slice(0, -1).join('/') || '/' : '/';
  const slug = slugifyVaultSegment(normalizedPath.split('/').pop() || input.title);
  const embedding = await generateEmbedding1536(`${input.title}\n\n${input.contentMarkdown}`);

  const payload: Record<string, unknown> = {
    vault_id: input.vaultId,
    parent_path: parentPath,
    path: normalizedPath,
    slug,
    title: input.title,
    page_type: input.pageType,
    content_markdown: input.contentMarkdown,
    summary: input.summary ?? null,
    source_document_ids: input.sourceDocumentIds ?? [],
    metadata: input.metadata ?? {},
  };

  if (embedding) {
    payload.embedding = embedding;
  }

  const { data, error } = await (supabase as any)
    .from('knowledge_base_vault_pages')
    .upsert(payload, { onConflict: 'vault_id,path' })
    .select('*')
    .single();

  if (error) throw error;

  const page = mapVaultPage(data);
  await syncVaultPageLinks(page);
  return page;
}

export async function syncVaultPageLinks(page: KnowledgeBaseVaultPage) {
  const links = extractVaultLinks(page.content_markdown);
  const allPages = await fetchVaultPages(page.vault_id);
  const linkRows = links.map((link) => {
    const match = allPages.find(
      (candidate) =>
        candidate.path === link.target ||
        `${candidate.slug}.md` === link.target ||
        candidate.slug === link.target.replace(/\.md$/i, '')
    );

    return {
      vault_id: page.vault_id,
      from_page_id: page.id,
      to_page_id: match?.id ?? null,
      target_path: link.target,
      link_text: link.linkText,
    };
  });

  const { error: deleteError } = await supabase
    .from('knowledge_base_vault_links')
    .delete()
    .eq('from_page_id', page.id);

  if (deleteError) throw deleteError;

  if (linkRows.length === 0) return;

  const { error: insertError } = await (supabase as any)
    .from('knowledge_base_vault_links')
    .insert(linkRows);

  if (insertError) throw insertError;
}

export async function createVaultRun(input: {
  vaultId: string;
  runType: VaultRunType;
  status: VaultRunStatus;
  inputDocumentIds?: string[];
  outputPageIds?: string[];
  notes?: string | null;
  createdBy?: string | null;
}) {
  const { data, error } = await (supabase as any)
    .from('knowledge_base_vault_runs')
    .insert({
      vault_id: input.vaultId,
      run_type: input.runType,
      status: input.status,
      input_document_ids: input.inputDocumentIds ?? [],
      output_page_ids: input.outputPageIds ?? [],
      notes: input.notes ?? null,
      created_by: input.createdBy ?? null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return mapVaultRun(data);
}

export async function updateVaultRun(runId: string, updates: {
  status?: VaultRunStatus;
  outputPageIds?: string[];
  notes?: string | null;
}) {
  const { data, error } = await (supabase as any)
    .from('knowledge_base_vault_runs')
    .update({
      status: updates.status,
      output_page_ids: updates.outputPageIds,
      notes: updates.notes,
    })
    .eq('id', runId)
    .select('*')
    .single();

  if (error) throw error;
  return mapVaultRun(data);
}

export async function fetchKnowledgeGraphNodes(knowledgeBaseId: string) {
  const { data, error } = await (supabase as any)
    .from('knowledge_graph_nodes')
    .select('*')
    .eq('knowledge_base_id', knowledgeBaseId)
    .order('node_type')
    .order('label');

  if (error) throw error;
  return (data ?? []).map(mapKnowledgeGraphNode);
}

export async function fetchKnowledgeGraphEdges(knowledgeBaseId: string) {
  const { data, error } = await (supabase as any)
    .from('knowledge_graph_edges')
    .select('*')
    .eq('knowledge_base_id', knowledgeBaseId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []).map(mapKnowledgeGraphEdge);
}

export async function upsertKnowledgeGraphNode(input: {
  knowledgeBaseId: string;
  nodeKey: string;
  nodeType: KnowledgeGraphNodeType;
  label: string;
  slug?: string | null;
  summary?: string | null;
  contentExcerpt?: string | null;
  vaultId?: string | null;
  vaultPageId?: string | null;
  sourceDocumentId?: string | null;
  vaultRunId?: string | null;
  metadata?: Record<string, unknown>;
  createdBy?: string | null;
}) {
  const embedding = await generateEmbedding(
    [input.label, input.summary ?? '', input.contentExcerpt ?? ''].filter(Boolean).join('\n\n').slice(0, 3000)
  );

  const payload: Record<string, unknown> = {
    knowledge_base_id: input.knowledgeBaseId,
    node_key: input.nodeKey,
    node_type: input.nodeType,
    label: input.label,
    slug: input.slug ?? null,
    summary: input.summary ?? null,
    content_excerpt: input.contentExcerpt ?? null,
    vault_id: input.vaultId ?? null,
    vault_page_id: input.vaultPageId ?? null,
    source_document_id: input.sourceDocumentId ?? null,
    vault_run_id: input.vaultRunId ?? null,
    metadata: input.metadata ?? {},
    created_by: input.createdBy ?? null,
  };

  if (embedding) {
    payload.embedding = embedding;
  }

  const { data, error } = await (supabase as any)
    .from('knowledge_graph_nodes')
    .upsert(payload, { onConflict: 'knowledge_base_id,node_key' })
    .select('*')
    .single();

  if (error) throw error;
  return mapKnowledgeGraphNode(data);
}

export async function upsertKnowledgeGraphEdge(input: {
  knowledgeBaseId: string;
  fromNodeId: string;
  toNodeId: string;
  edgeType: KnowledgeGraphEdgeType;
  weight?: number;
  evidence?: string | null;
  metadata?: Record<string, unknown>;
  createdBy?: string | null;
}) {
  const { data, error } = await (supabase as any)
    .from('knowledge_graph_edges')
    .upsert(
      {
        knowledge_base_id: input.knowledgeBaseId,
        from_node_id: input.fromNodeId,
        to_node_id: input.toNodeId,
        edge_type: input.edgeType,
        weight: input.weight ?? 1,
        evidence: input.evidence ?? null,
        metadata: input.metadata ?? {},
        created_by: input.createdBy ?? null,
      },
      { onConflict: 'knowledge_base_id,from_node_id,to_node_id,edge_type' }
    )
    .select('*')
    .single();

  if (error) throw error;
  return mapKnowledgeGraphEdge(data);
}

export async function matchKnowledgeGraphNodes(input: {
  knowledgeBaseId: string;
  queryEmbedding: number[];
  threshold?: number;
  count?: number;
  nodeTypes?: KnowledgeGraphNodeType[] | null;
}) {
  const { data, error } = await (supabase.rpc as any)('match_knowledge_graph_nodes', {
    query_embedding: input.queryEmbedding,
    match_threshold: input.threshold ?? 0.18,
    match_count: input.count ?? 8,
    p_knowledge_base_id: input.knowledgeBaseId,
    p_node_types: input.nodeTypes ?? null,
  });

  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    node: mapKnowledgeGraphNode(row),
    similarity: Number(row.similarity ?? 0),
  }));
}